import { config } from '@app/config';
import { getQueryParam } from '@mi-front/core';
import debugFn from 'debug';
import posthog, { PostHogConfig } from 'posthog-js';
import { PostHogContext, PostHogProvider } from 'posthog-js/react';
import { useMemo } from 'react';

const debug = debugFn('posthog');

const options: Partial<PostHogConfig> = {
  api_host: config.posthogHost,
  autocapture: false,
  // We handle pageview manually in our screen tracking hook for precision
  capture_pageview: false,
  // Useful for tracking session duration
  capture_pageleave: false,
  defaults: '2025-05-24',
  // Show real input values in session recordings, except password fields
  session_recording: {
    maskAllInputs: false,
    maskInputOptions: { password: true },
  },
  loaded: (ph) => {
    // Event-level super property only; person-level app_version is set in identify.
    ph.register({ app_version: config.appVersion });
  },
};

// Tracks whether the global PostHog instance has already been initialized, so we only call
// init() once even if this provider unmounts and remounts (e.g. login -> logout -> login).
let initialized = false;

/**
 * Initializes PostHog and exposes it through the React provider.
 *
 * PostHog is initialized synchronously during render (before any child effect runs) and is
 * handed to PostHogProvider through the `client` prop. This guarantees the instance is loaded
 * before usePostHogIdentify() calls identify() in a child effect.
 *
 * Why this matters: previously we passed the `apiKey` prop, which makes PostHogProvider call
 * init() inside its own effect. Since React flushes child effects before parent effects, the
 * identify() call ran against an uninitialized instance and was silently dropped, leaving users
 * anonymous (showing only the auto-generated UUID instead of their email).
 *
 * This wrapper is mounted only inside the authenticated app, so PostHog (and session recording)
 * never runs on the /login and /logout screens.
 */
export function PostHogProviderWrapper({ children }: { children: React.ReactNode }) {
  const client = useMemo(() => {
    const shouldEnable =
      (config.isNodeProd && config.posthogKey) || getQueryParam('enable_posthog');

    if (!shouldEnable) {
      debug('PostHog is disabled');
      return undefined;
    }

    if (!initialized) {
      debug('Initializing PostHog');
      posthog.init(config.posthogKey!, options);
      initialized = true;
    }

    return posthog;
  }, []);

  if (!client) {
    // Provide an empty context so usePostHog() returns undefined when PostHog is disabled.
    return (
      <PostHogContext.Provider
        value={
          { client: undefined, bootstrap: undefined } as unknown as React.ComponentProps<
            typeof PostHogContext.Provider
          >['value']
        }
      >
        {children}
      </PostHogContext.Provider>
    );
  }

  return <PostHogProvider client={client}>{children}</PostHogProvider>;
}
